import { Injectable } from '@angular/core';
import { ApiReturn_TokenRefresh, App_FullUri } from '../../utils/interfaces';
import { environment } from 'src/environments/environment';
import { HttpParams } from '@capacitor/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { arrayIfNullOrUndefined, dirtyb64ToBlob } from '../../utils/utils';
import { DataService } from './data.service';
import { Observable, catchError, concatMap, defer, firstValueFrom, map, mergeMap, of, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  constructor(
    private dataService: DataService,
    private httpClient: HttpClient,
  ) { }

  // GET

  apiGet<T>(fullUri: App_FullUri, _headers: [string, string][] = [], params: HttpParams | undefined) {
    const uri = this.uriFrom(fullUri);
    const headers = this.makeHeader(_headers);
    return this.httpClient.get<T>(uri, { headers, params });
  }

  // PUT

  apiPut<C, R>(fullUri: App_FullUri, credentials: C, _headers: [string, string][] = []) {
    const uri = this.uriFrom(fullUri);
    const headers = this.makeHeader(_headers);
    return this.httpClient.put<R>(uri, credentials, { headers });
  }

  // POST

  apiPost<C, R>(fullUri: App_FullUri, credentials: C, _headers: [string, string][] = []) {
    const uri = this.uriFrom(fullUri);
    const headers = this.makeHeader(_headers);
    return this.httpClient.post<R>(uri, credentials, { headers });
  }

  makeHeader(_headers: [string, string][]): HttpHeaders {
    return arrayIfNullOrUndefined(_headers).reduce((acc, cur) => acc.append(cur[0], cur[1]), new HttpHeaders());
  }

  uriFrom(fullUri: App_FullUri) {
    return (fullUri.uriHead !== undefined ? fullUri.uriHead : environment.apiUrl) + fullUri.uriTail;
  }

  dataGetUserToken(): Promise<string> {
    // TODO-pf, faire une déco forcée ou un autre protocole pour forcer le user à se reconnecter pour récupérer ses identifiants
    return firstValueFrom(this.dataService.userCredentials.get()
      .pipe(
        catchError(err => throwError(() => new Error('Échec de récupération du token de connexion utilisateur'))),
        concatMap(user => {
          const token = user?.token;
          if (typeof token === 'string') {
            const splittedToken = token.split('.');
            const fragment = splittedToken[1];
            const json = atob(fragment);
            const parsed = JSON.parse(json);
            const dateExp = new Date(parsed.exp * 1000);
            const nowDate = new Date();
            const ecart = dateExp.getTime() - nowDate.getTime();
            if (ecart <= 0) {
              const token_refresh = user?.token_refresh
              if (token_refresh) {
                return this.apiTokenRefresh(token_refresh)
              } else {
                return throwError(() => new Error('Token de connexion périmé'));
              }
            }
            return of(token);
          } else {
            return throwError(() => new Error('Échec de récupération du token de connexion utilisateur'))
          }
        })
      )
    );
  }

  apiTokenRefresh(token_refresh: string): Observable<string> {
    const credentials = {
      'token_refresh': token_refresh
    };
    return this.apiPost<{ token_refresh: string }, ApiReturn_TokenRefresh>({ uriTail: `/user/refresh-token` }, credentials).pipe(
      mergeMap(res => {
        const { token, token_refresh, ...rest } = res;
        return defer(() => Promise.all([
          this.dataService.user.set(rest),
          this.dataService.userCredentials.set({token: token, token_refresh: token_refresh})
        ])).pipe(map(_ => res.token))
      })
    );
  }

  /**
   * Login embed (émulateur). Le backend doit renvoyer la même structure user que /user/login,
   * notamment `droits` (ex. DROIT_ELABORATION_LEVAGE), sans quoi les contrôles d’accès (ex. création de plan) échoueront.
   *
   * Route d’entrée : `/embed?token=…&redirectafter=…` avec param optionnel `site_id` (voir embedAuthGuard) :
   * le parent intégrateur doit l’ajouter pour verrouiller le site (cohérent avec le contexte du levage ouvert).
   */
  apiLoginBack(token: string): Observable<string> {
    const credentials = { token };
    return this.apiPost<{ token: string }, ApiReturn_TokenRefresh>({ uriTail: `/user/login-back` }, credentials).pipe(
      mergeMap(res => {
        const { token: t, token_refresh, ...user } = res;
        return defer(() => Promise.all([
          this.dataService.user.set(user),
          this.dataService.userCredentials.set({ token: t, token_refresh })
        ])).pipe(map(() => res.token));
      })
    );
  }

  downloadImage(imgUrl: string): Observable<Blob> {
    return this.httpClient.get(imgUrl, { responseType: `blob` });
  }

  downloadMedia(mediaUrl: string): Observable<Blob> {
    return this.httpClient.get(mediaUrl, { responseType: `blob` });
  }

  downloadDocument(imgUrl: string): Observable<Blob> {
    return this.httpClient.get(imgUrl, { responseType: `blob` });
  }

}
